import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, LogOut } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Logo, initials } from "./common";
import { EASE } from "./motion";

function NavList({ items, onNavigate, layoutId }) {
  return (
    <nav className="flex flex-col gap-1">
      {items.map(({ to, label, icon: Icon, end, testid }) => (
        <NavLink key={to} to={to} end={end} onClick={onNavigate} data-testid={testid} className="relative">
          {({ isActive }) => (
            <div className={`group relative flex items-center gap-3 rounded-xl px-3.5 h-11 text-sm font-semibold transition-colors duration-200 ${isActive ? "text-[var(--ink)]" : "text-slate-500 hover:text-[var(--ink)] hover:bg-slate-50"}`}>
              {isActive && (
                <motion.div layoutId={layoutId} className="absolute inset-0 rounded-xl bg-white shadow-[0_6px_18px_-10px_rgba(15,23,42,0.35)] ring-1 ring-slate-200/80" transition={{ type: "spring", stiffness: 420, damping: 34 }}>
                  <span className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-[var(--brand)]" />
                </motion.div>
              )}
              <Icon className={`relative h-[18px] w-[18px] transition-transform duration-300 group-hover:scale-110 ${isActive ? "text-[var(--brand)]" : ""}`} strokeWidth={1.8} />
              <span className="relative">{label}</span>
            </div>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

function SideContent({ items, onNavigate, layoutId, badge }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const out = async () => {
    await logout();
    nav("/giris", { replace: true });
  };
  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-3 px-2 pb-7">
        <Logo variant="mark" className="h-9 w-auto" />
        <div className="leading-tight">
          <p className="text-[15px] font-extrabold tracking-tight text-[var(--ink)]">SK Enstitü</p>
          <p className="text-[11px] font-medium text-slate-400">{badge}</p>
        </div>
      </div>
      <NavList items={items} onNavigate={onNavigate} layoutId={layoutId} />
      <div className="mt-auto pt-6">
        <div className="mb-3 flex items-center gap-3 rounded-xl bg-white/70 p-3 ring-1 ring-slate-200/70">
          <div className="grid h-9 w-9 place-items-center rounded-full bg-[var(--ink)] text-xs font-bold text-white">{initials(user?.name)}</div>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-[var(--ink)]" data-testid="sidebar-user-name">{user?.name}</p>
            <p className="truncate text-[11px] text-slate-400">{user?.email}</p>
          </div>
        </div>
        <button onClick={out} data-testid="logout-btn" className="group flex w-full items-center gap-3 rounded-xl px-3.5 h-11 text-sm font-semibold text-slate-500 transition-colors hover:bg-rose-50 hover:text-rose-600">
          <LogOut className="h-[18px] w-[18px] transition-transform duration-300 group-hover:translate-x-0.5" strokeWidth={1.8} /> Çıkış Yap
        </button>
      </div>
    </div>
  );
}

export default function Shell({ items, badge }) {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => {
    setOpen(false);
  }, [loc.pathname]);
  return (
    <div className="min-h-screen bg-[var(--canvas)]">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-[272px] border-r border-slate-200/70 bg-[#fbfbfd] px-4 py-6 lg:block">
        <SideContent items={items} layoutId="nav-ind-desktop" badge={badge} />
      </aside>
      <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-slate-200/70 bg-white/80 px-4 backdrop-blur-xl lg:hidden">
        <div className="flex items-center gap-2.5">
          <Logo variant="mark" className="h-8 w-auto" />
          <span className="text-[15px] font-extrabold tracking-tight">SK Enstitü</span>
        </div>
        <button onClick={() => setOpen(true)} className="grid h-10 w-10 place-items-center rounded-xl ring-1 ring-slate-200 bg-white active:scale-95 transition-transform" data-testid="mobile-menu-btn" aria-label="Menü">
          <Menu className="h-5 w-5" />
        </button>
      </header>
      <AnimatePresence>
        {open && (
          <>
            <motion.div className="fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-sm lg:hidden" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setOpen(false)} data-testid="mobile-menu-backdrop" />
            <motion.aside className="fixed inset-y-0 left-0 z-50 w-[84%] max-w-[300px] bg-[#fbfbfd] px-4 py-6 shadow-2xl lg:hidden" initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }} transition={{ duration: 0.35, ease: EASE }} data-testid="mobile-drawer">
              <button onClick={() => setOpen(false)} className="absolute right-3 top-4 grid h-9 w-9 place-items-center rounded-lg hover:bg-slate-100" data-testid="mobile-menu-close-btn" aria-label="Kapat">
                <X className="h-5 w-5" />
              </button>
              <SideContent items={items} onNavigate={() => setOpen(false)} layoutId="nav-ind-mobile" badge={badge} />
            </motion.aside>
          </>
        )}
      </AnimatePresence>
      <main className="lg:pl-[272px]">
        <motion.div key={loc.pathname} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, ease: EASE }} className="mx-auto max-w-[1240px] px-4 py-7 sm:px-6 lg:px-10 lg:py-10">
          <Outlet />
        </motion.div>
      </main>
    </div>
  );
}
